import { useState, useEffect, useCallback, useRef } from 'react';
import { handleFetchError, makeAuthenticatedRequest } from '../utils/apiHelpers';
import { transformGenerationData } from '../utils/helpers';
import { transformBulkProjectData } from '../utils/bulkDataTransformer';

const API_URL = process.env.REACT_APP_API_URL;

// localStorage cache helpers
const getCachedData = (cacheKey, maxAge = 24 * 60 * 60 * 1000) => {
  try {
    const cachedData = localStorage.getItem(cacheKey);
    if (!cachedData) return null;
    const parsedData = JSON.parse(cachedData);
    if (Date.now() - parsedData.timestamp >= maxAge) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return parsedData.data;
  } catch {
    return null;
  }
};

const setCachedData = (cacheKey, data) => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

const useManageData = ({ token, isLoggedIn, projectId, navigate, location, onLogout }) => {
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const [generations, setGenerations] = useState([]);
  const [bulkGenerations, setBulkGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [bulkExcelData, setBulkExcelData] = useState(null);
  const [bulkDataLoading, setBulkDataLoading] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [selectedBulkProject, setSelectedBulkProject] = useState(null);
  const [showBulkResults, setShowBulkResults] = useState(false);

  const fetchGenerations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/auth/user_history?request_type=single_generate`;
      const response = await makeAuthenticatedRequest(url, token);
      if (!response.data?.data || !Array.isArray(response.data.data)) {
        setError('Received invalid data format from server');
        return;
      }
      const validItems = response.data.data.filter(item =>
        !item.is_deleted && (
          item.is_draft ||
          item.request_type === 'draft' ||
          item.request_type === 'bulk_draft' ||
          (item.status === 'Success' && (item.claude_output || item.output)) ||
          (item.status && item.status !== 'Success') ||
          (item.claude_output?.error || item.claude_output?.error_message)
        )
      );
      const singleItems = validItems.filter(item => item.request_type !== 'bulk_draft');
      const bulkDraftItems = validItems.filter(item => item.request_type === 'bulk_draft');

      if (projectId) {
        const selected = response.data.data.find(p => p.editor_folder_id === projectId);
        if (selected) {
          setSelectedGeneration(transformGenerationData(selected));
          setShowResults(true);
        }
      }
      setGenerations(singleItems);
      if (bulkDraftItems.length > 0) {
        setBulkGenerations(prev => {
          const existingIds = new Set(prev.map(b => b._id));
          const newItems = bulkDraftItems.filter(d => !existingIds.has(d._id));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
      }
    } catch (err) {
      handleFetchError(err, 'Failed to fetch generation data', setError);
    } finally {
      setLoading(false);
    }
  }, [token, projectId]);

  const fetchBulkGenerations = useCallback(async () => {
    setBulkLoading(true);
    setBulkError(null);
    try {
      const url = `${API_URL}/auth/user_history?request_type=bulk_generate&slim=true&limit=50`;
      const response = await makeAuthenticatedRequest(url, token);
      if (!response.data?.data || !Array.isArray(response.data.data)) {
        setBulkError('Received invalid data format from server');
        return;
      }
      const valid = response.data.data.filter(item =>
        !item.is_deleted && (
          (item.status === 'Completed' && (item.image_urls || item.output_files || item.has_image_urls || item.has_output_files)) ||
          (item.status && item.status !== 'Completed')
        )
      );
      const seenIds = new Set();
      const deduped = valid.filter(item => {
        if (seenIds.has(item._id)) return false;
        seenIds.add(item._id);
        return true;
      });
      setBulkGenerations(deduped);
    } catch (err) {
      handleFetchError(err, 'Failed to fetch bulk generation data', setBulkError);
    } finally {
      setBulkLoading(false);
    }
  }, [token]);

  const loadBulkExcelData = useCallback(async (bulkProject) => {
    const hasData = bulkProject?.output_json || bulkProject?.output_files?.length || bulkProject?.excel_file_url;
    const hasImageUrls = bulkProject?.image_urls?.parent_skus?.length;
    if (!hasData || !hasImageUrls) {
      setBulkExcelData(null);
      setBulkDataLoading(false);
      return;
    }
    const cacheKey = `bulk_data_v3_${bulkProject._id}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setBulkExcelData(cachedData);
      setBulkDataLoading(false);
      return;
    }
    setBulkDataLoading(true);
    try {
      const { productData } = await transformBulkProjectData(bulkProject);
      setCachedData(cacheKey, productData);
      setBulkExcelData(productData);
    } catch {
      setBulkExcelData(null);
    } finally {
      setBulkDataLoading(false);
    }
  }, []);

  const handleContentUpdated = useCallback(async (updatedContent) => {
    try {
      await fetchGenerations();
      if (updatedContent?._isChildUpdate) return;
      if (selectedGeneration) {
        setSelectedGeneration({
          ...selectedGeneration,
          claude_output: { ...selectedGeneration.claude_output, ...updatedContent }
        });
      }
    } catch (error) {
    }
  }, [fetchGenerations, selectedGeneration]);

  // Auth effect
  useEffect(() => {
    if (isLoggedIn) {
      fetchGenerations();
      fetchBulkGenerations();
    } else {
      setError('Not authenticated. Please log in.');
      setBulkError('Not authenticated. Please log in.');
      setLoading(false);
      setBulkLoading(false);
      if (onLogoutRef.current) onLogoutRef.current();
    }
  }, [isLoggedIn, fetchGenerations, fetchBulkGenerations]);

  // Load Excel data when bulk project is selected
  useEffect(() => {
    if (selectedBulkProject && !bulkExcelData && !bulkDataLoading) {
      loadBulkExcelData(selectedBulkProject);
    }
  }, [selectedBulkProject, loadBulkExcelData, bulkExcelData, bulkDataLoading]);

  // Refetch bulk project data on bfcache restore / tab switch
  useEffect(() => {
    let hiddenAt = null;
    const refetchIfNeeded = async () => {
      if (!selectedBulkProject?._id || !showBulkResults) return;
      localStorage.removeItem(`bulk_data_v3_${selectedBulkProject._id}`);
      setBulkExcelData(null);
      try {
        const url = `${API_URL}/auth/user_history/${selectedBulkProject._id}`;
        const response = await makeAuthenticatedRequest(url, token);
        if (response.data?.data) {
          const fullProject = response.data.data;
          setBulkGenerations(prev => prev.map(p => p._id === fullProject._id ? fullProject : p));
          setSelectedBulkProject(fullProject);
          loadBulkExcelData(fullProject);
        }
      } catch {
        // Silently fall back to existing data
      }
    };
    const handlePageShow = (e) => { if (e.persisted) refetchIfNeeded(); };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt) {
        if (Date.now() - hiddenAt > 3000) refetchIfNeeded();
        hiddenAt = null;
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedBulkProject, showBulkResults, token, loadBulkExcelData]);

  // Handle navigation state for opening projects
  useEffect(() => {
    const { openGenerationId, projectId: navProjectId, bulkProjectId } = location.state || {};

    if (openGenerationId && generations.length > 0) {
      const generation = generations.find(gen => gen._id === openGenerationId);
      if (generation) {
        setSelectedGeneration(transformGenerationData(generation));
        setShowResults(true);
        navigate(location.pathname, { replace: true });
      }
      return;
    }

    if (navProjectId && generations.length > 0) {
      const generation = generations.find(gen =>
        gen.editor_folder_id === navProjectId || gen._id === navProjectId
      );
      if (generation) {
        setSelectedGeneration(transformGenerationData(generation));
        setShowResults(true);
        navigate(location.pathname, { replace: true, state: {} });
      }
      return;
    }

    if (bulkProjectId && bulkGenerations.length > 0) {
      const bulkProject = bulkGenerations.find(bulk => bulk._id === bulkProjectId);
      if (bulkProject) {
        if (!bulkProject.image_urls) {
          (async () => {
            try {
              const url = `${API_URL}/auth/user_history/${bulkProject._id}`;
              const response = await makeAuthenticatedRequest(url, token);
              if (response.data?.data) {
                const fullProject = response.data.data;
                setBulkGenerations(prev => prev.map(p => p._id === fullProject._id ? fullProject : p));
                setSelectedBulkProject(fullProject);
              }
            } catch {
              setSelectedBulkProject(bulkProject);
            }
          })();
        } else {
          setSelectedBulkProject(bulkProject);
        }
        setShowBulkResults(true);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [generations, bulkGenerations, location.state, navigate, loadBulkExcelData, token]);

  // Close result cards when clicking Projects in sidebar
  useEffect(() => {
    if (location.state?.resetView) {
      setSelectedGeneration(null);
      setShowResults(false);
      setShowBulkResults(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.resetView, navigate, location.pathname]);

  return {
    generations, setGenerations,
    bulkGenerations, setBulkGenerations,
    loading, error, bulkLoading, bulkError,
    bulkExcelData, setBulkExcelData, bulkDataLoading,
    selectedGeneration, setSelectedGeneration, showResults, setShowResults,
    selectedBulkProject, setSelectedBulkProject, showBulkResults, setShowBulkResults,
    fetchGenerations, fetchBulkGenerations, loadBulkExcelData,
    handleContentUpdated,
  };
};

export default useManageData;
